import React, { useEffect, useMemo, useRef, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Collapse,
  Divider,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

import Captcha from "@/components/Captcha";
import { formConfig } from "./utils/formConfig";
import { getSteps } from "./utils/getSteps";
import { stepFields } from "./utils/stepFields";

import StepperIcon from "./components/StepperIcon";
import {
  editButtonMdStyles,
  editButtonSmStyles,
  stepperStyles,
} from "./styles";
import {
  useAddPayNowMutation,
  useGetShippingQuery,
} from "@/redux-store/api/marketingApi.ts";
import { useAppDispatch, useAppSelector } from "@/redux-store/hooks.ts";
import { setPaymentResponse } from "@/redux-store/cart/cartSlice.ts";
import Notification from "@/components/Notification/Notification.tsx";
import { formatPhoneNumberIntl } from "@/utils/formatPhoneNumberIntl.ts";
import FormPayStep from "@/components/Checkout/GuestCheckout/steps/FormPayStep.tsx";
import Loader from "@/components/Shared/loader";
import { CaptchaRef } from "@/components/Captcha/Captcha";

interface IProps {
  goToConfirmation: () => void;
  orderSummary: IOrderSummary;
  setOrderSummary: React.Dispatch<React.SetStateAction<IOrderSummary>>;
  checkoutData?: ICheckoutForm | undefined;
  setCheckoutData: React.Dispatch<React.SetStateAction<ICheckoutForm | null>>;
}

const GuestCheckout: React.FC<IProps> = ({
  goToConfirmation,
  setOrderSummary,
  checkoutData,
  orderSummary,
  // setCheckoutData,
}) => {
  const FIRST_STEP = 0;
  const LAST_STEP = 2;
  const [activeStep, setActiveStep] = useState<number>(FIRST_STEP);
  // Form control using React Hook Form
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const methods = useForm<ICheckoutForm>(formConfig(checkoutData));
  const { handleSubmit, watch, getValues, reset, trigger } = methods;
  const shippingMethod = watch().shippingMethod;
  const component = useMemo(() => {
    if (checkoutData?.paymentMethods?.length > 0) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      return (props: any) => (
        <FormPayStep {...props} paymentMethods={checkoutData?.paymentMethods} />
      );
    }
    return null;
  }, checkoutData?.paymentMethods);

  const formSteps = getSteps(component);
  const [payNowMutation] = useAddPayNowMutation();
  const { data: ship } = useGetShippingQuery({});
  const sessionId = useAppSelector((state) => state.cart.sessionId);
  const cartId = useAppSelector((state) => state.cart.cartId);
  const dispatch = useAppDispatch();
  const [notificationIsOpen, setNotificationIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [notification, setNotification] = useState({
    open: false,
    title: "",
    description: "",
  });

  const [captchaValid, setCaptchaValid] = useState<ICaptchaValid>({
    valid: false,
    value: "",
  });

  useEffect(() => {
    if (checkoutData !== undefined)
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      reset(formConfig(checkoutData).defaultValues);
  }, [checkoutData, reset]);

  // Update shipping cost in order summary on Shipping method change
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const shipping = ship?.find((option) => shippingMethod == option.id);
    if (shipping) {
      setOrderSummary((prevState) => ({
        ...prevState,
        shipping: shipping.price,
      }));
    }
  }, [setOrderSummary, shippingMethod]);

  const captchaRef = useRef<CaptchaRef>(null);
  const handleCaptchaVerify = (captcha: ICaptchaValid) => {
    setCaptchaValid(captcha);
  };

  // Handle submit form data
  const onSubmit: SubmitHandler<ICheckoutForm> = async (
    data: ICheckoutForm
  ) => {
    setNotificationIsOpen(true);
    setIsSubmitting(true);
    if (captchaValid.valid) {
      const transformedData = {
        idUser: checkoutData ? checkoutData?.idUser : null,
        sessionId: sessionId,
        cartId: cartId,
        paymentDetails: {
          customerPaymentGatewayNumber:
            checkoutData?.customerPaymentNumber !== null &&
            checkoutData?.customerPaymentNumber !== undefined
              ? checkoutData.customerPaymentNumber
              : "",
          paymentMethodId: checkoutData ? String(data.paymentMethodId) : "",
          cardNumber:
            checkoutData?.paymentMethods?.length > 0
              ? null
              : data?.number?.replace(/\s/g, ""),
          cardHolder:
            checkoutData?.paymentMethods?.length > 0 ? null : data.cardholder,
          cvv: checkoutData?.paymentMethods?.length > 0 ? null : data.cvc,
          expDate:
            checkoutData?.paymentMethods?.length > 0
              ? null
              : data?.expiry?.replace(" / ", "/"),
        },
        billingDetails: {
          firstName: data.billingAddress!
            ? data.firstName
            : data.billingFirstName,
          lastName: data.billingAddress! ? data.lastName : data.billingLastName,
          companyName: data.billingAddress!
            ? data.companyName
            : data.billingCompanyName,
          email: data.billingAddress! ? data.email : data.billingEmail,
          phoneNumber: data.billingAddress!
            ? formatPhoneNumberIntl(data.phoneNumber)
            : formatPhoneNumberIntl(data.billingPhoneNumber),
          zipCode: data.billingAddress! ? data.zipCode : data.billingZipCode,
          city: data.billingAddress! ? data.city : data.billingCity,
          street: data.billingAddress!
            ? data.streetAddress
            : data.billingStreetAddress,
          address: data.billingStreetAddress!
            ? data.billingStreetAddress + " " + data.billingCity
            : data.streetAddress + " " + data.city,
          idCountry: data.billingAddress!
            ? data.idCountry
            : data.billingIdCountry,
          idState: data.billingAddress! ? data.idState : data.billingIdState,
        },
        shippingDetails: {
          shippingMethodId: Number(data.shippingMethod),
          firstName: data.firstName,
          lastName: data.lastName,
          companyName: data.companyName,
          email: data.email,
          phoneNumber: formatPhoneNumberIntl(data.phoneNumber),
          zipCode: data.zipCode,
          city: data.city,
          street: data.streetAddress,
          address: `${data.streetAddress} ${data.city}`,
          idCountry: data.idCountry,
          idState: data.idState,
        },
        promoCodeId: orderSummary.promoCodeId || null,
        captcha: captchaValid.value,
      };

      try {
        const response = await payNowMutation(transformedData).unwrap();
        dispatch(setPaymentResponse(response));
        reset();
        captchaRef.current?.resetCaptcha();
        goToConfirmation();
      } catch (e) {
        captchaRef.current?.resetCaptcha();
        const error = e as { data: string };
        setNotification({
          open: true,
          title: "Error",
          description: error.data,
        });
      } finally {
        setIsSubmitting(false);
        setTimeout(() => {
          setNotificationIsOpen(false);
        }, 2500);
      }
    } else {
      setIsSubmitting(false);
    }
  };

  // Checks whether current step fields are valid and go to the next step
  const handleNext = async () => {
    // Get fields to validate for the current step
    const fieldsToValidate = stepFields[activeStep];

    // Trigger validation for fields in the current step
    let isValidStep = true;
    for (const field of fieldsToValidate) {
      isValidStep = isValidStep && (await trigger(field));
    }

    if (isValidStep) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  return (
    <>
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        display="flex"
        flexDirection="column"
        maxWidth={662}
        mx="auto"
      >
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={stepperStyles}
        >
          {formSteps.map(({ label, Component, Summary }, index) => (
            <Step key={index}>
              {/* Step label */}
              <StepLabel
                icon={<StepperIcon index={index} activeStep={activeStep} />}
                sx={{
                  pt: index !== 0 ? 4 : 1,
                  pb: activeStep < index ? 4 : 1,
                }}
              >
                <Box display="flex" width="100%" justifyContent="space-between">
                  <Typography variant="h5" component="p">
                    {label}
                  </Typography>
                  {activeStep > index && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => setActiveStep(index)}
                      sx={editButtonMdStyles}
                    >
                      Edit
                    </Button>
                  )}
                </Box>
              </StepLabel>

              {/* Step summary */}
              <Collapse in={activeStep > index}>
                {activeStep > index && (
                  <Box pb={{ xs: 4 }}>
                    {Summary && (
                      <Box pl={6} pt={{ xs: 2, md: 0 }} pb={{ xs: 3, md: 0 }}>
                        <Summary getValues={getValues} />
                      </Box>
                    )}
                    <Button
                      variant="outlined"
                      color="secondary"
                      fullWidth
                      onClick={() => setActiveStep(index)}
                      sx={editButtonSmStyles}
                    >
                      Edit
                    </Button>
                  </Box>
                )}
              </Collapse>

              {/* Step Content */}
              <StepContent>
                <Box pt={{ xs: 2 }} pb={{ xs: 4 }}>
                  <Component methods={methods} />

                  {activeStep < LAST_STEP && (
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      onClick={handleNext}
                      sx={{ mt: { xs: 4, md: 3 } }}
                    >
                      Continue
                    </Button>
                  )}
                </Box>
              </StepContent>

              {activeStep !== LAST_STEP && <Divider />}
            </Step>
          ))}
        </Stepper>

        {activeStep === LAST_STEP && (
          <>
            <Captcha onVerify={handleCaptchaVerify} ref={captchaRef} />
            {isSubmitting ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100vh"
                width="100vw"
              >
                <Loader />
              </Box>
            ) : (
              <Button
                variant="contained"
                fullWidth
                color="primary"
                type="submit"
                disabled={!captchaValid.valid}
                sx={{ marginTop: "32px" }}
              >
                Pay Now
              </Button>
            )}
          </>
        )}
      </Box>
      {notification.title && notification.description && (
        <Notification
          open={notificationIsOpen}
          onClose={() => setNotificationIsOpen(false)}
          title={notification.title}
          description={notification.description}
          severity="error"
        />
      )}
    </>
  );
};

export default GuestCheckout;
